import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { usePurchasePrice } from "@/contexts/PurchasePriceContext";
import { useNotifications } from "@/contexts/NotificationContext";
import { useTheme } from "@/contexts/ThemeContext";
import ErrorBoundary from "@/components/feature/ErrorBoundary";
import RestaurantQuickStockModal from "@/components/feature/RestaurantQuickStockModal";

type NavItemChild = { path: string; label: string };
type NavItem = { path?: string; icon: string; label: string; children?: NavItemChild[] };

const baseNavItems = [
  {
    group: "Ana Menü",
    items: [
      { path: "/dashboard", icon: "ri-dashboard-line", label: "Dashboard" },
      { path: "/sales", icon: "ri-shopping-cart-2-line", label: "Satış" },
      { path: "/products", icon: "ri-box-3-line", label: "Ürünler" },
      { path: "/categories", icon: "ri-price-tag-3-line", label: "Kategoriler" },
    ] as NavItem[],
  },
  {
    group: "Yönetim",
    items: [
      { path: "/suppliers", icon: "ri-truck-line", label: "Tedarikçiler" },
      { path: "/dealers", icon: "ri-user-star-line", label: "Cari Bayiler" },
    ] as NavItem[],
  },
  {
    group: "Raporlar & İşlemler",
    items: [
      {
        icon: "ri-bar-chart-2-line",
        label: "Raporlar",
        children: [
          { path: "/reports/sales-purchases", label: "Satışlar - Alışlar" },
          { path: "/reports/financial", label: "Finansal Raporlar" },
          { path: "/reports/stock", label: "Stok Raporları" },
          { path: "/reports/customers", label: "Müşteri Listesi" },
        ],
      },
      { path: "/invoices", icon: "ri-file-list-3-line", label: "Faturalar" },
      { path: "/accounting", icon: "ri-calculator-line", label: "Muhasebe" },
      { path: "/support", icon: "ri-customer-service-2-line", label: "Destek" },
      { path: "/payment-notifications", icon: "ri-notification-3-line", label: "Ödeme Bildirimleri" },
      { path: "/backup", icon: "ri-database-2-line", label: "Yedekleme" },
    ] as NavItem[],
  },
];

const restaurantNavGroup = (user: any) => ({
  group: "Restoran Modülü",
  items: [
    { path: "/restaurant-pos", icon: "ri-restaurant-line", label: "Restoran POS" },
    ...(user?.hasRestaurantStock ? [{ path: "/restaurant/stock", icon: "ri-archive-line", label: "Stok Yönetimi" }] : []),
    ...(user?.hasRestaurantBranches ? [{ path: "/restaurant/branches", icon: "ri-store-2-line", label: "Şube Yönetimi" }] : []),
    ...(user?.hasRestaurantReports ? [{ path: "/reports/restaurant", icon: "ri-pie-chart-line", label: "Restoran Raporu" }] : []),
  ] as NavItem[],
});

const dealerAdminNavItems = [
  {
    group: "Bayi Yönetimi",
    items: [
      { path: "/dealer-admin/currencies", icon: "ri-money-dollar-circle-line", label: "Para Birimi" },
      { path: "/dealer-admin/users", icon: "ri-user-star-line", label: "Bayi Kullanıcıları" },
      { path: "/dealer-admin/staff", icon: "ri-team-line", label: "Personel Yönetimi" },
      { path: "/dealer-admin/branches", icon: "ri-store-2-line", label: "Şube Yönetimi" },
      { path: "/categories", icon: "ri-price-tag-3-line", label: "Kategori Yönetimi" },
      { path: "/dealer-admin/logs", icon: "ri-file-list-3-line", label: "İşlem Logları" },
      { path: "/dealer-admin/bank-accounts", icon: "ri-bank-line", label: "Banka Tanımlamaları" },
      { path: "/dealer-admin/import", icon: "ri-upload-cloud-2-line", label: "Veri Aktarımı" },
      { path: "/dealer-admin/stock-import",     icon: "ri-file-excel-2-line",    label: "Stok İçe Aktar"    },
      { path: "/dealer-admin/payment-settings", icon: "ri-secure-payment-line",  label: "Sanal POS Ayarları" },
    ] as NavItem[],
  },
  {
    group: "Stok Yönetimi",
    items: [
      { path: "/restaurant/stock", icon: "ri-archive-line", label: "Stok Yönetimi" },
    ] as NavItem[],
  },
  {
    group: "Restoran Modülü",
    items: [
      { path: "/restaurant-pos", icon: "ri-restaurant-line", label: "Restoran POS" },
      { path: "/reports/restaurant", icon: "ri-pie-chart-line", label: "Restoran Raporu" },
      { path: "/restaurant/import-sales", icon: "ri-file-upload-line",  label: "Satış İçe Aktar"     },
      { path: "/restaurant/elektraweb",   icon: "ri-links-line",         label: "ElektraWeb Entegrasyon" },
    ] as NavItem[],
  },
];

export default function B2BLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const [openSubmenus, setOpenSubmenus] = useState<Record<string, boolean>>({});
  const {
    showPurchasePrice,
    hidePurchasePrice,
    isPasswordModalOpen,
    openPasswordModal,
    closePasswordModal,
    verifyAndShow,
  } = usePurchasePrice();
  const { notifications, unseenCount, markAllSeen } = useNotifications();
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const { theme, resolvedTheme, toggleTheme } = useTheme();
  const [showStockModal, setShowStockModal] = useState(false);
  const [rates, setRates] = useState<{ usd: number; eur: number; gbp: number } | null>(null);

  const isDealerAdmin = user?.loginType === "dealer_admin";
  const isAvullarRestaurant = user?.dealerName?.toLowerCase().includes("avullar");
  const isMGDealer =
    !isDealerAdmin &&
    (user?.dealerCode?.toLowerCase() === "mg" ||
     user?.dealerName?.toLowerCase().includes("mg teknik") ||
     user?.dealerName?.toLowerCase() === "mg");

  // MG bayisi için özel nav
  const mgNavItems = [
    {
      group: "Yönetim",
      items: [
        { path: "/support", icon: "ri-customer-service-2-line", label: "Destek" },
        { path: "/backup",  icon: "ri-database-2-line",         label: "Yedekleme" },
      ] as NavItem[],
    },
    {
      group: "Depo Modülü",
      items: [
        { path: "/warehouse/pos",           icon: "ri-store-3-line",       label: "Depo POS"      },
        { path: "/warehouse/crm",           icon: "ri-customer-service-2-line", label: "Teknik Servis CRM" },
        { path: "/warehouse/stock",         icon: "ri-box-3-line",         label: "Depo Stok"     },
        { path: "/warehouse/model-control", icon: "ri-list-settings-line", label: "Model Kontrol"  },
        { path: "/warehouse/reports/stock", icon: "ri-bar-chart-box-line", label: "Stok Raporu"   },
        { path: "/warehouse/reports/sales", icon: "ri-receipt-line",       label: "Satış Raporu"  },
        { path: "/warehouse/setup",         icon: "ri-settings-3-line",    label: "Depo Kurulumu" },
      ] as NavItem[],
    },
  ];

  useEffect(() => {
    if (!isAvullarRestaurant) return;
    let cancelled = false;

    const fetchRates = async () => {
      try {
        const res = await fetch("https://api.frankfurter.app/latest?from=TRY&to=USD,EUR,GBP");
        const data = await res.json();
        if (!cancelled && data?.rates?.USD && data?.rates?.EUR && data?.rates?.GBP) {
          setRates({
            usd: 1 / data.rates.USD,
            eur: 1 / data.rates.EUR,
            gbp: 1 / data.rates.GBP,
          });
        }
      } catch {
        // Keep last known rates on failure; ticker simply won't update this cycle
      }
    };

    fetchRates();
    const interval = setInterval(fetchRates, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isAvullarRestaurant]);

  const navItems = isDealerAdmin
    ? dealerAdminNavItems
    : isMGDealer
      ? mgNavItems
      : (isAvullarRestaurant
          ? [restaurantNavGroup(user)]
          : (user?.hasRestaurant
            ? [...baseNavItems, restaurantNavGroup(user)]
            : baseNavItems));

  useEffect(() => {
    navItems.forEach((group) => {
      group.items.forEach((item) => {
        if (item.children) {
          const hasActiveChild = item.children.some(
            (c) => location.pathname === c.path || location.pathname.startsWith(c.path + "/")
          );
          if (hasActiveChild) {
            setOpenSubmenus((prev) => ({ ...prev, [item.label]: true }));
          }
        }
      });
    });
  }, [location.pathname, navItems]);

  const toggleSubmenu = (label: string) => {
    setOpenSubmenus((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const isSubmenuActive = (item: NavItem) => {
    if (!item.children) return false;
    return item.children.some(
      (c) => location.pathname === c.path || location.pathname.startsWith(c.path + "/")
    );
  };

  const handleToggleClick = () => {
    if (showPurchasePrice) {
      hidePurchasePrice();
    } else {
      openPasswordModal();
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passwordInput.trim()) return;

    setPasswordLoading(true);
    setPasswordError("");

    const result = await verifyAndShow(passwordInput);

    if (!result.success) {
      setPasswordError(result.message);
    }

    setPasswordInput("");
    setPasswordLoading(false);
  };

  const handleCloseModal = () => {
    closePasswordModal();
    setPasswordInput("");
    setPasswordError("");
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const displayName = isDealerAdmin
    ? user?.dealerName
    : user?.loginType === "dealer_user"
      ? user?.fullName
      : user?.dealerName;

  const subLabel = isDealerAdmin
    ? "Bayi Admin | " + user?.dealerCode
    : user?.loginType === "dealer_user"
      ? `${user?.role || "Kullanıcı"} | ${user?.dealerName}`
      : user?.dealerCode;

  return (
    <div className="h-screen flex bg-background-50 overflow-hidden">
      {/* Sidebar Overlay Mobile */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        ></div>
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-background-50 border-r border-background-200/70 flex flex-col transition-transform duration-300 ${
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        } ${sidebarOpen ? "lg:w-64" : "lg:w-20"}`}
      >
        {/* Sidebar Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-background-200/70 shrink-0">
          <div className={`flex items-center gap-3 ${!sidebarOpen && "lg:hidden"}`}>
            <img
              src="https://public.readdy.ai/ai/img_res/0bc3c21d-f175-4039-a061-cbb43a619b2e.png"
              alt="ART POS"
              className="w-11 h-11 object-contain"
            />
            <span className="font-bold text-foreground-950 text-sm whitespace-nowrap">ART POS</span>
          </div>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="hidden lg:flex w-8 h-8 items-center justify-center rounded-md hover:bg-background-100 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
          >
            <i className={sidebarOpen ? "ri-contract-left-right-line" : "ri-expand-left-right-line"}></i>
          </button>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="lg:hidden w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 text-foreground-400 cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Nav Items */}
        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-4">
          {navItems.map((group) => (
            <div key={group.group}>
              {sidebarOpen && (
                <p className="text-xs font-medium text-foreground-400 uppercase tracking-wider px-3 mb-2">
                  {group.group}
                </p>
              )}
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  item.children ? (
                    <div key={item.label}>
                      <button
                        onClick={() => toggleSubmenu(item.label)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors whitespace-nowrap cursor-pointer ${
                          isSubmenuActive(item)
                            ? "bg-primary-100 text-primary-700 font-medium"
                            : "text-foreground-600 hover:bg-background-100 hover:text-foreground-900"
                        } ${!sidebarOpen ? "lg:justify-center lg:px-0" : ""}`}
                      >
                        <i className={`${item.icon} text-lg shrink-0`}></i>
                        <span className={!sidebarOpen ? "lg:hidden" : ""}>{item.label}</span>
                        <i className={`ri-arrow-down-s-line ml-auto text-sm transition-transform duration-200 ${openSubmenus[item.label] ? "rotate-180" : ""} ${!sidebarOpen ? "lg:hidden" : ""}`}></i>
                      </button>
                      {(openSubmenus[item.label] && sidebarOpen) && (
                        <div className="ml-2 mt-0.5 space-y-0.5 border-l-2 border-background-200/50 pl-2">
                          {item.children.map((child) => (
                            <NavLink
                              key={child.path}
                              to={child.path}
                              onClick={() => setMobileMenuOpen(false)}
                              className={({ isActive }) =>
                                `flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors whitespace-nowrap ${
                                  isActive
                                    ? "bg-primary-100 text-primary-700 font-medium"
                                    : "text-foreground-600 hover:bg-background-100 hover:text-foreground-900"
                                }`
                              }
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-current opacity-50"></span>
                              {child.label}
                            </NavLink>
                          ))}
                        </div>
                      )}
                      {(openSubmenus[item.label] && !sidebarOpen) && (
                        <div className="lg:hidden ml-2 mt-0.5 space-y-0.5 border-l-2 border-background-200/50 pl-2">
                          {item.children.map((child) => (
                            <NavLink
                              key={child.path}
                              to={child.path}
                              onClick={() => setMobileMenuOpen(false)}
                              className={({ isActive }) =>
                                `flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors whitespace-nowrap ${
                                  isActive
                                    ? "bg-primary-100 text-primary-700 font-medium"
                                    : "text-foreground-600 hover:bg-background-100 hover:text-foreground-900"
                                }`
                              }
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-current opacity-50"></span>
                              {child.label}
                            </NavLink>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <NavLink
                      key={item.path}
                      to={item.path!}
                      onClick={() => setMobileMenuOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors whitespace-nowrap ${
                          isActive
                            ? "bg-primary-100 text-primary-700 font-medium"
                            : "text-foreground-600 hover:bg-background-100 hover:text-foreground-900"
                        } ${!sidebarOpen ? "lg:justify-center lg:px-0" : ""}`
                      }
                    >
                      <i className={`${item.icon} text-lg shrink-0`}></i>
                      <span className={!sidebarOpen ? "lg:hidden" : ""}>{item.label}</span>
                    </NavLink>
                  )
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Sidebar Footer */}
        <div className="border-t border-background-200/70 p-3">
          <div className={`flex items-center gap-3 px-3 py-2 ${!sidebarOpen && "lg:justify-center lg:px-0"}`}>
            <div className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center shrink-0">
              <i className="ri-user-line text-foreground-500 text-sm"></i>
            </div>
            <div className={`flex-1 min-w-0 ${!sidebarOpen && "lg:hidden"}`}>
              <p className="text-sm font-medium text-foreground-900 truncate">
                {displayName}
              </p>
              <p className="text-xs text-foreground-400">
                {subLabel}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className={`w-full mt-2 flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-500 hover:bg-background-100 hover:text-foreground-700 transition-colors cursor-pointer whitespace-nowrap ${
              !sidebarOpen ? "lg:justify-center lg:px-0" : ""
            }`}
          >
            <i className="ri-logout-box-line text-lg shrink-0"></i>
            <span className={!sidebarOpen ? "lg:hidden" : ""}>Çıkış Yap</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        {/* Top Bar */}
        <header className="h-16 bg-background-50 border-b border-background-200/70 flex items-center justify-between px-4 md:px-6 shrink-0 relative">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-md hover:bg-background-100 text-foreground-500 cursor-pointer shrink-0"
            >
              <i className="ri-menu-line text-xl"></i>
            </button>

            {!isAvullarRestaurant && <div className="flex-1 lg:flex-none"></div>}
          </div>

          {isAvullarRestaurant && (
            <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none items-center gap-4">
              {rates && (
                <div className="flex items-center gap-2.5 mr-3">
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-foreground-600 bg-background-100 px-2 py-1 rounded-full">
                    <i className="ri-money-dollar-circle-line text-emerald-500"></i>
                    USD {rates.usd.toFixed(2)}
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-foreground-600 bg-background-100 px-2 py-1 rounded-full">
                    <i className="ri-money-euro-circle-line text-blue-500"></i>
                    EUR {rates.eur.toFixed(2)}
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-foreground-600 bg-background-100 px-2 py-1 rounded-full">
                    <i className="ri-money-pound-circle-line text-violet-500"></i>
                    GBP {rates.gbp.toFixed(2)}
                  </span>
                </div>
              )}
              <span className="text-sm lg:text-base font-bold tracking-wide text-foreground-900 whitespace-nowrap">
                AVULLAR KONAKLAMA TESİSLERİ
              </span>
            </div>
          )}

          <div className="flex items-center gap-3">
            {/* Notification Bell */}
            <div className="relative">
              <button
                onClick={() => { setShowNotifDropdown(!showNotifDropdown); if (!showNotifDropdown) markAllSeen(); }}
                className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors cursor-pointer relative ${
                  unseenCount > 0
                    ? "bg-accent-100 text-accent-600 hover:bg-accent-200/70"
                    : "bg-background-100 text-foreground-400 hover:bg-background-200/70"
                }`}
                title="Stok Bildirimleri"
              >
                <i className="ri-notification-3-line text-lg"></i>
                {unseenCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-accent-500 text-background-50 text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
                    {unseenCount > 9 ? "9+" : unseenCount}
                  </span>
                )}
              </button>

              {showNotifDropdown && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowNotifDropdown(false)}></div>
                  <div className="absolute right-0 top-full mt-2 w-72 sm:w-80 bg-background-50 border border-background-200/70 rounded-xl shadow-lg z-50 max-h-96 overflow-hidden">
                    <div className="p-3 border-b border-background-200/70 flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-foreground-950">Stok Bildirimleri</h4>
                      <button onClick={() => setShowNotifDropdown(false)} className="w-6 h-6 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer">
                        <i className="ri-close-line text-sm"></i>
                      </button>
                    </div>
                    <div className="overflow-y-auto max-h-72">
                      {notifications.length === 0 ? (
                        <div className="text-center py-8 text-foreground-400">
                          <i className="ri-checkbox-circle-line text-3xl block mb-2"></i>
                          <p className="text-sm">Tüm stoklar yeterli</p>
                          <p className="text-xs mt-0.5">Düşük stoklu ürün bulunmuyor</p>
                        </div>
                      ) : (
                        <div className="py-1">
                          {notifications.map((n) => (
                            <div
                              key={n.productId}
                              className={`px-4 py-2.5 flex items-start gap-3 hover:bg-background-100 transition-colors ${!n.seen ? "bg-accent-50/50" : ""}`}
                            >
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                                n.type === "out" ? "bg-accent-100 text-accent-600" : "bg-secondary-100 text-secondary-600"
                              }`}>
                                <i className={n.type === "out" ? "ri-close-circle-line" : "ri-alert-line"}></i>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-foreground-900 truncate">{n.productName}</p>
                                <p className="text-xs text-foreground-400">
                                  {n.productCode ? `${n.productCode} | ` : ""}
                                  Stok: <strong className={n.type === "out" ? "text-accent-500" : "text-secondary-600"}>{n.stock}</strong>
                                </p>
                                <p className="text-xs text-foreground-500 mt-0.5">
                                  {n.type === "out" ? "Stok tükenmiş!" : "Düşük stok uyarısı"}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {notifications.length > 0 && (
                      <div className="p-2 border-t border-background-200/70">
                        <NavLink
                          to="/products"
                          onClick={() => setShowNotifDropdown(false)}
                          className="block text-center text-xs text-primary-600 hover:text-primary-700 font-medium py-1.5 rounded-md hover:bg-primary-50 transition-colors cursor-pointer"
                        >
                          Tüm ürünleri görüntüle
                        </NavLink>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {isAvullarRestaurant ? (
              <button
                onClick={() => setShowStockModal(true)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium bg-primary-100 text-primary-700 hover:bg-primary-200/70 transition-colors cursor-pointer whitespace-nowrap"
                title="Kategori ve ürün hızlı ekle"
              >
                <i className="ri-archive-line text-sm"></i>
                Stok
              </button>
            ) : (
              <button
                onClick={handleToggleClick}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  showPurchasePrice
                    ? "bg-accent-100 text-accent-700 hover:bg-accent-200/70"
                    : "bg-background-100 text-foreground-400 hover:bg-background-200/70"
                }`}
                title={showPurchasePrice ? "Alış fiyatlarını gizle" : "Alış fiyatlarını göster"}
              >
                <i className={`${showPurchasePrice ? "ri-eye-off-line" : "ri-eye-line"} text-sm`}></i>
                {showPurchasePrice ? "AF Gizle" : "AF Göster"}
              </button>
            )}
            <button
              onClick={toggleTheme}
              className="w-9 h-9 rounded-lg flex items-center justify-center bg-background-100 text-foreground-400 hover:bg-background-200/70 transition-colors cursor-pointer"
              title={
                theme === "system"
                  ? `Otomatik (${resolvedTheme === "dark" ? "Gece" : "Gündüz"})`
                  : theme === "dark"
                    ? "Gece Modu"
                    : "Gündüz Modu"
              }
            >
              <i className={
                resolvedTheme === "dark"
                  ? "ri-moon-line text-lg"
                  : "ri-sun-line text-lg"
              }></i>
            </button>
            <div className="hidden sm:flex items-center gap-2 text-sm text-foreground-500">
              <i className="ri-calendar-line"></i>
              <span>
                {new Date().toLocaleDateString("tr-TR", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </span>
            </div>
            <div className="w-px h-6 bg-background-200/70 hidden sm:block"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                <i className="ri-user-line text-primary-600 text-sm"></i>
              </div>
              <span className="text-sm font-medium text-foreground-700 hidden sm:block">
                {displayName}
              </span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-4 md:p-6 pb-20 lg:pb-6">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>

        {/* Mobile Bottom Navigation */}
        <nav className="fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-background-50 border-t border-background-200/70">
          <div className="flex items-center justify-around h-16 px-2">
            {isDealerAdmin ? (
              isAvullarRestaurant ? (
                <>
                  <NavLink
                    to="/restaurant-pos"
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex flex-col items-center justify-center gap-0.5 min-w-0 flex-1 py-1 transition-colors ${
                        isActive ? "text-primary-600" : "text-foreground-400"
                      }`
                    }
                  >
                    <i className="ri-restaurant-line text-xl"></i>
                    <span className="text-[10px] font-medium whitespace-nowrap">Restoran</span>
                  </NavLink>
                  <NavLink
                    to="/restaurant/stock"
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex flex-col items-center justify-center gap-0.5 min-w-0 flex-1 py-1 transition-colors ${
                        isActive ? "text-primary-600" : "text-foreground-400"
                      }`
                    }
                  >
                    <i className="ri-archive-line text-xl"></i>
                    <span className="text-[10px] font-medium whitespace-nowrap">Stok</span>
                  </NavLink>
                  <NavLink
                    to="/reports/restaurant"
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex flex-col items-center justify-center gap-0.5 min-w-0 flex-1 py-1 transition-colors ${
                        isActive ? "text-primary-600" : "text-foreground-400"
                      }`
                    }
                  >
                    <i className="ri-pie-chart-line text-xl"></i>
                    <span className="text-[10px] font-medium whitespace-nowrap">Rapor</span>
                  </NavLink>
                  <NavLink
                    to="/restaurant/import-sales"
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex flex-col items-center justify-center gap-0.5 min-w-0 flex-1 py-1 transition-colors ${
                        isActive ? "text-primary-600" : "text-foreground-400"
                      }`
                    }
                  >
                    <i className="ri-file-upload-line text-xl"></i>
                    <span className="text-[10px] font-medium whitespace-nowrap">İçe Aktar</span>
                  </NavLink>
                  <button
                    onClick={() => setMobileMenuOpen(true)}
                    className="flex flex-col items-center justify-center gap-0.5 min-w-0 flex-1 py-1 text-foreground-400 cursor-pointer"
                  >
                    <i className="ri-menu-line text-xl"></i>
                    <span className="text-[10px] font-medium whitespace-nowrap">Menü</span>
                  </button>
                </>
              ) : (
                <>
                  <NavLink
                    to="/dealer-admin/staff"
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex flex-col items-center justify-center gap-0.5 min-w-0 flex-1 py-1 transition-colors ${
                        isActive ? "text-primary-600" : "text-foreground-400"
                      }`
                    }
                  >
                    <i className="ri-team-line text-xl"></i>
                    <span className="text-[10px] font-medium whitespace-nowrap">Personel</span>
                  </NavLink>
                  <NavLink
                    to="/dealer-admin/branches"
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex flex-col items-center justify-center gap-0.5 min-w-0 flex-1 py-1 transition-colors ${
                        isActive ? "text-primary-600" : "text-foreground-400"
                      }`
                    }
                  >
                    <i className="ri-store-2-line text-xl"></i>
                    <span className="text-[10px] font-medium whitespace-nowrap">Şubeler</span>
                  </NavLink>
                  <NavLink
                    to="/restaurant/stock"
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex flex-col items-center justify-center gap-0.5 min-w-0 flex-1 py-1 transition-colors ${
                        isActive ? "text-primary-600" : "text-foreground-400"
                      }`
                    }
                  >
                    <i className="ri-archive-line text-xl"></i>
                    <span className="text-[10px] font-medium whitespace-nowrap">Stok</span>
                  </NavLink>
                  <NavLink
                    to="/dealer-admin/logs"
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex flex-col items-center justify-center gap-0.5 min-w-0 flex-1 py-1 transition-colors ${
                        isActive ? "text-primary-600" : "text-foreground-400"
                      }`
                    }
                  >
                    <i className="ri-file-list-3-line text-xl"></i>
                    <span className="text-[10px] font-medium whitespace-nowrap">Loglar</span>
                  </NavLink>
                  <button
                    onClick={() => setMobileMenuOpen(true)}
                    className="flex flex-col items-center justify-center gap-0.5 min-w-0 flex-1 py-1 text-foreground-400 cursor-pointer"
                  >
                    <i className="ri-menu-line text-xl"></i>
                    <span className="text-[10px] font-medium whitespace-nowrap">Menü</span>
                  </button>
                </>
              )
            ) : (
              isAvullarRestaurant ? (
                <>
                  <NavLink
                    to="/restaurant-pos"
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex flex-col items-center justify-center gap-0.5 min-w-0 flex-1 py-1 transition-colors ${
                        isActive ? "text-primary-600" : "text-foreground-400"
                      }`
                    }
                  >
                    <i className="ri-restaurant-line text-xl"></i>
                    <span className="text-[10px] font-medium whitespace-nowrap">Restoran</span>
                  </NavLink>
                  <NavLink
                    to="/restaurant/stock"
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex flex-col items-center justify-center gap-0.5 min-w-0 flex-1 py-1 transition-colors ${
                        isActive ? "text-primary-600" : "text-foreground-400"
                      }`
                    }
                  >
                    <i className="ri-archive-line text-xl"></i>
                    <span className="text-[10px] font-medium whitespace-nowrap">Stok</span>
                  </NavLink>
                  <NavLink
                    to="/reports/restaurant"
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex flex-col items-center justify-center gap-0.5 min-w-0 flex-1 py-1 transition-colors ${
                        isActive ? "text-primary-600" : "text-foreground-400"
                      }`
                    }
                  >
                    <i className="ri-pie-chart-line text-xl"></i>
                    <span className="text-[10px] font-medium whitespace-nowrap">Rapor</span>
                  </NavLink>
                  <NavLink
                    to="/restaurant/import-sales"
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex flex-col items-center justify-center gap-0.5 min-w-0 flex-1 py-1 transition-colors ${
                        isActive ? "text-primary-600" : "text-foreground-400"
                      }`
                    }
                  >
                    <i className="ri-file-upload-line text-xl"></i>
                    <span className="text-[10px] font-medium whitespace-nowrap">İçe Aktar</span>
                  </NavLink>
                  <button
                    onClick={() => setMobileMenuOpen(true)}
                    className="flex flex-col items-center justify-center gap-0.5 min-w-0 flex-1 py-1 text-foreground-400 cursor-pointer"
                  >
                    <i className="ri-menu-line text-xl"></i>
                    <span className="text-[10px] font-medium whitespace-nowrap">Menü</span>
                  </button>
                </>
              ) : (
                <>
                  <NavLink
                    to="/dashboard"
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex flex-col items-center justify-center gap-0.5 min-w-0 flex-1 py-1 transition-colors ${
                        isActive ? "text-primary-600" : "text-foreground-400"
                      }`
                    }
                  >
                    <i className="ri-dashboard-line text-xl"></i>
                    <span className="text-[10px] font-medium whitespace-nowrap">Ana Sayfa</span>
                  </NavLink>
                  <NavLink
                    to="/sales"
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex flex-col items-center justify-center gap-0.5 min-w-0 flex-1 py-1 transition-colors ${
                        isActive ? "text-primary-600" : "text-foreground-400"
                      }`
                    }
                  >
                    <i className="ri-shopping-cart-2-line text-xl"></i>
                    <span className="text-[10px] font-medium whitespace-nowrap">Satış</span>
                  </NavLink>
                  {user?.hasRestaurant && (
                  <NavLink
                    to="/restaurant-pos"
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex flex-col items-center justify-center gap-0.5 min-w-0 flex-1 py-1 transition-colors ${
                        isActive ? "text-primary-600" : "text-foreground-400"
                      }`
                    }
                  >
                    <i className="ri-restaurant-line text-xl"></i>
                    <span className="text-[10px] font-medium whitespace-nowrap">Restoran</span>
                  </NavLink>
                  )}
                  <NavLink
                    to="/products"
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex flex-col items-center justify-center gap-0.5 min-w-0 flex-1 py-1 transition-colors ${
                        isActive ? "text-primary-600" : "text-foreground-400"
                      }`
                    }
                  >
                    <i className="ri-box-3-line text-xl"></i>
                    <span className="text-[10px] font-medium whitespace-nowrap">Ürünler</span>
                  </NavLink>
                  <button
                    onClick={() => setMobileMenuOpen(true)}
                    className="flex flex-col items-center justify-center gap-0.5 min-w-0 flex-1 py-1 text-foreground-400 cursor-pointer"
                  >
                    <i className="ri-menu-line text-xl"></i>
                    <span className="text-[10px] font-medium whitespace-nowrap">Menü</span>
                  </button>
                </>
              )
            )}
          </div>
        </nav>
      </div>

      {/* Password Modal for showing purchase prices */}
      {isPasswordModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30" onClick={handleCloseModal}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-base font-semibold text-foreground-950">
                <a id="purchase-price-auth">Bayi Şifresi Gerekli</a>
              </h4>
              <button onClick={handleCloseModal} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
            <p className="text-sm text-foreground-500 mb-4">
              Alış fiyatlarını görüntülemek için bayi şifrenizi girin.
            </p>
            <form onSubmit={handlePasswordSubmit}>
              <div className="mb-3">
                <label className="block text-xs font-medium text-foreground-500 mb-1">Şifre</label>
                <input
                  type="password"
                  value={passwordInput}
                  onChange={(e) => { setPasswordInput(e.target.value); setPasswordError(""); }}
                  className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  placeholder="Bayi şifrenizi girin"
                  autoFocus
                />
              </div>
              {passwordError && (
                <div className="bg-accent-100 text-accent-700 text-xs rounded-lg px-3 py-2 mb-3 flex items-center gap-1.5">
                  <i className="ri-error-warning-line"></i>
                  {passwordError}
                </div>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={!passwordInput.trim() || passwordLoading}
                  className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  {passwordLoading ? (
                    <span className="flex items-center justify-center gap-1.5">
                      <i className="ri-loader-4-line animate-spin"></i>
                      Kontrol ediliyor
                    </span>
                  ) : (
                    "Onayla"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <RestaurantQuickStockModal open={showStockModal} onClose={() => setShowStockModal(false)} />
    </div>
  );
}